import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Toolbar,
  AppBar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import BarChartIcon from '@mui/icons-material/BarChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
} from 'recharts';
import { useUser } from '../hooks';
import { DrawerNavigation, MmcIcon, DRAWER_WIDTH, COLLAPSED_WIDTH } from '../components';
import { getTaskBgColor } from '../constants/taskConfig';
import { PageName, ApiResponse, ModelYearStatistic } from '../types';
import api from '../services/api';

// Custom tooltip for model-year scatter chart
const ModelYearTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="body2" fontWeight="bold">{data.model}</Typography>
        <Typography variant="body2" color="text.secondary">
          Year: {data.year}
        </Typography>
        <Typography variant="body2" color="primary">
          Count: {data.count.toLocaleString()}
        </Typography>
      </Paper>
    );
  }
  return null;
};

// Format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

interface MMCModelYearStatisticsProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function MMCModelYearStatistics({ onLogout, onNavigate }: MMCModelYearStatisticsProps) {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const { user, loading: userLoading } = useUser();

  // Statistics data state
  const [modelYears, setModelYears] = useState<ModelYearStatistic[]>([]);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Ref to prevent double fetching in StrictMode
  const hasFetched = useRef(false);

  // Fetch statistics data
  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiResponse<ModelYearStatistic[]>>('statistics/mmc/model-years');
      setModelYears(response.data.results);
    } catch (err) {
      console.error('Failed to fetch model-year statistics:', err);
      setError('Failed to load statistics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchStatistics();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  if (userLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Side Navigation Drawer */}
      <DrawerNavigation
        currentPage="statsMmcModelYear"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 0.2s, margin-left 0.2s',
          bgcolor: '#1976d2',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <BarChartIcon />
            <Typography variant="h6" noWrap component="div">
              MMC Model x Year
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchStatistics}
              disabled={loading}
            >
              Refresh
            </Button>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, ml: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 0.2s, margin-left 0.2s',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" disableGutters>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('mmc', theme.palette.mode),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MmcIcon size={40} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h1">
                  Model x Year Distribution
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Scatter plot of model-year combinations
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {modelYears.length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Combinations
                </Typography>
              </Box>
            </Box>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Model-Year Distribution - Scatter Chart */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Model-Year Distribution ({modelYears.length.toLocaleString()} combinations)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hover over points to see the model name. Y-axis uses logarithmic scale.
            </Typography>
            <Box sx={{ height: 600 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="year"
                    name="Year"
                    domain={['auto', 'auto']}
                    tickFormatter={(value) => value.toString()}
                  />
                  <YAxis
                    type="number"
                    dataKey="count"
                    name="Count"
                    scale="log"
                    domain={[1, 'auto']}
                    tickFormatter={formatNumber}
                  />
                  <ZAxis range={[20, 200]} />
                  <Tooltip content={<ModelYearTooltip />} />
                  <Scatter
                    name="Model-Year"
                    data={modelYears}
                    fill="#1976d2"
                    fillOpacity={0.6}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

export default MMCModelYearStatistics;
